import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ExtractionQAFileData, ExtractionQAGet, ExtractedByType } from '../../../../core/interfaces/admin-interfaces';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { QaAiToolsPanelComponent } from '../qa-ai-tools-panel/qa-ai-tools-panel.component';

interface FieldComparison {
  field: string;
  erik: any;
  neox: any;
  updated: any;
  erikMatch: boolean;
  neoxMatch: boolean;
  periodIndex: number; // -1 for shared/non-period fields
}

interface DisplayRow {
  type: 'comparison' | 'separator';
  comparison?: FieldComparison;
  periodLabel?: string;
}

@Component({
  selector: 'app-expanded-extraction-qa',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, CustomDropdownComponent, QaAiToolsPanelComponent],
  templateUrl: './expanded-extraction-qa.component.html',
  styleUrl: './expanded-extraction-qa.component.css'
})
export class ExpandedExtractionQAComponent implements OnChanges {
  constructor(private translocoService: TranslocoService) {}

  @Input() qaData: ExtractionQAFileData | null = null;
  @Input() toast!: ToastComponent;
  @Input() invoicePeriodType: string = 'mensile';

  erikRecord: ExtractionQAGet | null = null;
  neoxRecord: ExtractionQAGet | null = null;
  updatedRecord: ExtractionQAGet | null = null;

  fieldComparisons: FieldComparison[] = [];

  // Computed stats
  totalFields: number = 0;
  erikCorrect: number = 0;
  neoxCorrect: number = 0;
  erikAccuracy: number = 0;
  neoxAccuracy: number = 0;
  erikImprovement: number = 0;
  improvementDiff: number = 0;

  get isBimestrale(): boolean {
    return this.invoicePeriodType === 'bimestrale';
  }

  // Table filter: 0=All, 1=V1 mismatches, 2=V2 mismatches, 3=Both mismatches
  filterDropdownOptions: string[] = [];
  selectedFilterIndex: number = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['qaData'] && this.qaData) {
      this.processRecords();
      this.computeComparisons();
    }
  }

  private processRecords(): void {
    if (!this.qaData) return;

    this.erikRecord = this.qaData.qa_records.find(r => r.extracted_by === ExtractedByType.ERIK) || null;
    this.neoxRecord = this.qaData.qa_records.find(r => r.extracted_by === ExtractedByType.NEOX) || null;
    this.updatedRecord = this.qaData.qa_records.find(r => r.extracted_by === ExtractedByType.UPDATED) || null;
  }

  private computeComparisons(): void {
    this.fieldComparisons = [];

    if (!this.updatedRecord) return;

    const updatedFlat = this.flattenObject(this.updatedRecord.extraction_data || {});
    const erikFlat = this.flattenObject(this.erikRecord?.extraction_data || {});
    const neoxFlat = this.flattenObject(this.neoxRecord?.extraction_data || {});

    let erikCorrectCount = 0;
    let neoxCorrectCount = 0;

    // Only iterate over Updated keys with non-null values (matching backend _compute_accuracy)
    for (const key of Object.keys(updatedFlat)) {
      const updatedVal = updatedFlat[key];
      if (updatedVal == null || this.shouldIgnoreField(key)) continue;

      const erikVal = erikFlat[key];
      const neoxVal = neoxFlat[key];

      const erikMatch = this.valuesMatch(erikVal, updatedVal);
      const neoxMatch = this.valuesMatch(neoxVal, updatedVal);

      if (erikMatch) erikCorrectCount++;
      if (neoxMatch) neoxCorrectCount++;

      this.fieldComparisons.push({
        field: key,
        erik: erikVal,
        neox: neoxVal,
        updated: updatedVal,
        erikMatch,
        neoxMatch,
        periodIndex: this.extractPeriodIndex(key)
      });
    }

    const stats = this.qaData?.stats;
    this.totalFields = stats?.total_fields || this.fieldComparisons.length;
    this.erikCorrect = stats?.correct_fields_old || 0;
    this.neoxCorrect = stats?.correct_fields_new || 0;
    this.erikAccuracy = stats?.accuracy_old || 0;
    this.neoxAccuracy = stats?.accuracy_new || 0;
    this.erikImprovement = this.neoxAccuracy - this.erikAccuracy;
    this.improvementDiff = this.neoxCorrect - this.erikCorrect;

    this.buildFilterOptions();
  }

  private buildFilterOptions(): void {
    if (this.isBimestrale) {
      this.filterDropdownOptions = [
        this.translocoService.translate('admin.qa.filter_all') + ` (${this.fieldComparisons.length})`,
        this.translocoService.translate('admin.qa.filter_v2_mismatches') + ` (${this.v2MismatchCount})`,
      ];
    } else {
      this.filterDropdownOptions = [
        this.translocoService.translate('admin.qa.filter_all') + ` (${this.fieldComparisons.length})`,
        this.translocoService.translate('admin.qa.filter_v1_mismatches') + ` (${this.v1MismatchCount})`,
        this.translocoService.translate('admin.qa.filter_v2_mismatches') + ` (${this.v2MismatchCount})`,
        this.translocoService.translate('admin.qa.filter_both_mismatches') + ` (${this.bothMismatchCount})`,
      ];
    }
  }

  get filteredComparisons(): FieldComparison[] {
    if (this.isBimestrale) {
      switch (this.selectedFilterIndex) {
        case 1: return this.fieldComparisons.filter(c => !c.neoxMatch);
        default: return this.fieldComparisons;
      }
    }
    switch (this.selectedFilterIndex) {
      case 1: return this.fieldComparisons.filter(c => !c.erikMatch);
      case 2: return this.fieldComparisons.filter(c => !c.neoxMatch);
      case 3: return this.fieldComparisons.filter(c => !c.erikMatch || !c.neoxMatch);
      default: return this.fieldComparisons;
    }
  }

  get v1MismatchCount(): number {
    return this.fieldComparisons.filter(c => !c.erikMatch).length;
  }

  get v2MismatchCount(): number {
    return this.fieldComparisons.filter(c => !c.neoxMatch).length;
  }

  get bothMismatchCount(): number {
    return this.fieldComparisons.filter(c => !c.erikMatch || !c.neoxMatch).length;
  }

  private extractPeriodIndex(key: string): number {
    const match = key.match(/periods\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : -1;
  }

  get displayRows(): DisplayRow[] {
    const comparisons = this.filteredComparisons;
    if (!this.isBimestrale) {
      return comparisons.map(c => ({ type: 'comparison' as const, comparison: c }));
    }

    const rows: DisplayRow[] = [];
    const shared = comparisons.filter(c => c.periodIndex === -1);
    const periodMap = new Map<number, FieldComparison[]>();

    for (const c of comparisons) {
      if (c.periodIndex >= 0) {
        if (!periodMap.has(c.periodIndex)) {
          periodMap.set(c.periodIndex, []);
        }
        periodMap.get(c.periodIndex)!.push(c);
      }
    }

    // Shared fields first
    if (shared.length > 0) {
      rows.push(...shared.map(c => ({ type: 'comparison' as const, comparison: c })));
    }

    // Period groups
    const sortedPeriods = Array.from(periodMap.keys()).sort((a, b) => a - b);
    for (const periodIdx of sortedPeriods) {
      const periodFields = periodMap.get(periodIdx)!;
      const periodLabel = this.buildPeriodLabel(periodIdx, periodFields);
      rows.push({ type: 'separator' as const, periodLabel });
      rows.push(...periodFields.map(c => ({ type: 'comparison' as const, comparison: c })));
    }

    return rows;
  }

  private buildPeriodLabel(periodIndex: number, fields: FieldComparison[]): string {
    // Try to find start/end month from the period fields in Updated record
    const startMonth = fields.find(f => f.field.includes('start_month'))?.updated;
    const endMonth = fields.find(f => f.field.includes('end_month'))?.updated;
    const startDate = fields.find(f => f.field.includes('start_date'))?.updated;
    const endDate = fields.find(f => f.field.includes('end_date'))?.updated;

    if (startMonth && endMonth) {
      return `Period ${periodIndex + 1}: ${startMonth} - ${endMonth}`;
    }
    if (startDate && endDate) {
      return `Period ${periodIndex + 1}: ${startDate} - ${endDate}`;
    }
    return `Period ${periodIndex + 1}`;
  }

  private readonly IGNORED_FIELDS = ['historical_data', 'id', 'processed_at', 'invoice_data.additional_charges'];

  private shouldIgnoreField(key: string): boolean {
    return this.IGNORED_FIELDS.some(field => key.includes(field));
  }

  private flattenObject(obj: any, prefix: string = ''): { [key: string]: any } {
    const result: { [key: string]: any } = {};

    for (const key of Object.keys(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(result, this.flattenObject(value, fullKey));
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item !== null && typeof item === 'object') {
            Object.assign(result, this.flattenObject(item, `${fullKey}[${index}]`));
          } else {
            result[`${fullKey}[${index}]`] = item;
          }
        });
      } else {
        result[fullKey] = value;
      }
    }

    return result;
  }

  private valuesMatch(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;

    // Compare as strings for loose matching
    const strA = String(a).trim().toLowerCase();
    const strB = String(b).trim().toLowerCase();
    return strA === strB;
  }

  translateField(fieldKey: string): string {
    const segments = fieldKey.split('.');
    const lastSegment = segments[segments.length - 1].replace(/\[\d+\]$/, '');

    const translationKey = `invoice.labels.${lastSegment}`;
    const translated = this.translocoService.translate(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    return lastSegment
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  getMatchClass(match: boolean): string {
    return match ? 'text-success-500' : 'text-error-500';
  }

  getExtractedClass(value: number, other: number): string {
    if (value > other) return 'text-success-500';
    if (value < other) return 'text-error-500';
    return '';
  }
}
